import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircleIcon, LockKeyhole, Save } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import LoginButtons from 'app/frontend/base/components/LoginButtons'
import Select from 'app/frontend/base/components/Select'
import { useTeam } from 'app/frontend/base/config/context/team'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { createViewerEventParticipant } from 'app/frontend/base/api/event'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useApplyEventUpdate from '../hooks/useApplyEventUpdate'
import { CharacterRole } from 'app/frontend/types/custom'
import {
  getSelectableRolesForCharacter,
  getViewerSelectableCharacters,
  SignupRoleSelect,
  SignupStatusSelect,
  viewerSignupCharacterSelectOptions,
} from './viewerSignupShared'

type Props = {
  event: wowaudit.Event
  user?: wowaudit.User
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
}

const ViewerNewParticipantSignupCard = ({
  event,
  user,
  selectableStatuses,
}: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const applyEventUpdate = useApplyEventUpdate()
  const [createStatus, setCreateStatus] = useState<
    wowaudit.EventParticipantReference['status']
  >(selectableStatuses[0] || 'present')
  const [createRole, setCreateRole] = useState<CharacterRole>('melee')
  const [createComment, setCreateComment] = useState('')
  const [createCharacterId, setCreateCharacterId] = useState<string>()
  const createMutation = useMutation({
    mutationFn: async ({
      status,
      comment,
      characterId,
      role,
    }: {
      status: wowaudit.EventParticipantReference['status']
      comment: string
      characterId: number
      role: CharacterRole
    }) => {
      return await createViewerEventParticipant(team.id, {
        eventId: event.id,
        status,
        comment,
        characterId,
        role,
      })
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
      setCreateComment('')
      setCreateCharacterId(undefined)
    },
    onError: (error) => {
      displayErrorToast(error as Error, t)
    },
  })

  if (!user?.id) {
    return (
      <Card className='w-fit flex flex-wrap gap-8 pt-2 mb-2'>
        <div>
          <h3 className='m-0 text-base font-semibold'>
            {t('dashboard.team.events.viewer-signup.title')}
          </h3>

          <p className='text-sm text-muted-foreground'>
            {t('dashboard.team.events.viewer-signup.login-required')}
          </p>
        </div>

        <LoginButtons withLabel={false} />
      </Card>
    )
  }

  const { effectiveGuestPolicy, selectableCharacters } =
    getViewerSelectableCharacters({
      event,
      team,
      user,
    })
  const selectedCharacter = selectableCharacters.find(
    (character) => character.id.toString() === createCharacterId,
  )
  const selectableRoles = useMemo(() => {
    return getSelectableRolesForCharacter(selectedCharacter?.classId)
  }, [selectedCharacter?.classId])
  const canSignUp =
    (effectiveGuestPolicy === 'only_with_characters_in_team' ||
      effectiveGuestPolicy === 'with_any_character' ||
      effectiveGuestPolicy === 'only_with_owned_characters') &&
    selectableCharacters.length > 0

  return (
    <Card className='w-fit mb-3 pt-2'>
      <h3 className='mt-0 mb-2 text-base font-semibold'>
        Sign up for this event
      </h3>

      {canSignUp ? (
        <div className='flex flex-wrap items-center gap-2'>
          <Select
            value={createCharacterId}
            setValue={(value) => {
              setCreateCharacterId(value)
              if (!value) return
              const selectedCharacter = selectableCharacters.find(
                (character) => character.id.toString() === value,
              )
              if (!selectedCharacter) return
              const nextRoles = getSelectableRolesForCharacter(
                selectedCharacter.classId,
              )
              if (!nextRoles.length) return
              if (nextRoles.includes(selectedCharacter.role)) {
                setCreateRole(selectedCharacter.role)
                return
              }
              setCreateRole(nextRoles[0])
            }}
            disabled={createMutation.isPending || event.status === 'locked'}
            options={viewerSignupCharacterSelectOptions(selectableCharacters)}
            className='w-[270px]'
            placeholder={t(
              'dashboard.team.events.viewer-signup.select-character',
            )}
          />
          <SignupStatusSelect
            statuses={selectableStatuses}
            value={createStatus}
            setValue={setCreateStatus}
            disabled={createMutation.isPending || event.status === 'locked'}
            className='w-[220px]'
          />
          <SignupRoleSelect
            value={createRole}
            setValue={setCreateRole}
            roles={selectableRoles}
            disabled={
              createMutation.isPending ||
              event.status === 'locked' ||
              !createCharacterId ||
              selectableRoles.length === 0
            }
            className='w-[170px]'
          />
          <Input
            value={createComment}
            disabled={createMutation.isPending || event.status === 'locked'}
            onChange={(event) => setCreateComment(event.target.value)}
            placeholder='Comment'
            className='w-[240px]'
          />
          {event.status === 'locked' ? (
            <Badge variant='info' className='h-8 px-2.5 gap-1.5'>
              <LockKeyhole className='w-3.5 h-3.5' />
              Locked
            </Badge>
          ) : (
            <Button
              size='sm'
              disabled={!createCharacterId || createMutation.isPending}
              onClick={() => {
                if (!createCharacterId) return
                createMutation.mutate({
                  status: createStatus,
                  role: createRole,
                  comment: createComment,
                  characterId: Number(createCharacterId),
                })
              }}
            >
              <Save className='w-4 h-4 mr-1' />
              Save
            </Button>
          )}
        </div>
      ) : (
        <Alert variant='warning'>
          <AlertCircleIcon className='h-4 w-4' />
          <AlertTitle>Signup unavailable</AlertTitle>
          <AlertDescription>
            {effectiveGuestPolicy === 'only_with_characters_in_team'
              ? t('dashboard.team.events.viewer-signup.no-team-characters')
              : t('dashboard.team.events.viewer-signup.signup-disabled')}
          </AlertDescription>
        </Alert>
      )}

      <p className='text-xs text-muted-foreground mt-4'>
        {t('dashboard.team.events.viewer-signup.character-sync')}{' '}
        <Link to='/profile/characters' className='underline'>
          {t('dashboard.team.events.viewer-signup.character-sync-link')}
        </Link>
      </p>
    </Card>
  )
}

export default ViewerNewParticipantSignupCard
